import { FloatingBackButton } from "@components/atoms/FloatingBackButton"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { ChipList } from "@components/layout/ChipList"
import { RouteFlippedResponsiveImage } from "@components/motion/RouteFlipped"
import { StaggerTransitionRoot } from "@components/motion/Transition"
import { IconAwards } from "@components/svg/Icons"
import { MakeURL } from "@utils/create-url"
import { Link } from "eddev/routing"
import { defineView } from "eddev/views"
import { PropsWithChildren } from "react"

export default defineView("single-award-entry", (props) => {
  const entry = props.awardEntry!

  return (
    <StaggerTransitionRoot key={entry.id}>
      <FloatingBackButton />
      <div className="grid-auto">
        <div className="col-span-12 md:col-start-8 md:col-span-4 md:order-1">
          <div className="md:sticky md:top-headerHeight md:h-pageMin md:overflow-auto hide-scrollbars pt-7 md:pb-5 flex flex-col justify-between" data-transition="children/fade">
            <div>
              <div className="stack-x-2 items-center type-title-s-medium border-b pb-3">
                {entry.merit !== "Finalist" && <IconAwards className="-ml-1" />}
                <div>{entry.merit}</div>
              </div>
              <h1 className="type-title-l-medium mt-5 mb-3 md:mb-4">{entry.title}</h1>
              <ChipList
                chips={[
                  {
                    label: String(entry.year)!,
                    url: MakeURL.awardSearch({ year: entry.year! }),
                  },
                  {
                    label: entry.category?.parent?.title!,
                  },
                  {
                    label: entry.client?.name!,
                  },
                  {
                    label: entry.studio?.name!,
                    url: entry.studio?.uri!,
                  },
                ].filter((c) => !!c.label)}
              />
            </div>
            <div className="pt-6 stack-y-4">
              {!!entry.category?.title && <MetaRow label="Subcategory">{entry.category?.title}</MetaRow>}
              {!!entry.submittingMember?.name && (
                <MetaRow label="Submitted by">
                  {entry.submittingMember.uri ? <Link href={entry.submittingMember.uri}>{entry.submittingMember.name}</Link> : entry.submittingMember.name}
                </MetaRow>
              )}
              {entry.contributors?.length! > 0 && (
                <MetaRow label="Contributors">
                  <div className="stack-y-3">
                    {entry.contributors?.map((item, i) => {
                      return (
                        <div key={i}>
                          <div>{getContributorLabel(item?.type!)}</div>
                          <div className="whitespace-pre-wrap">{item?.name}</div>
                        </div>
                      )
                    })}
                  </div>
                </MetaRow>
              )}
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-start-2 md:col-span-6  pt-7">
          {entry.artworks?.map((artwork, i) => (
            <div data-transition="fade" key={i} className="mb-7">
              {i === 0 ? (
                <RouteFlippedResponsiveImage flipId={entry.id} srcSet={artwork?.srcSet!} aspect={artwork?.aspect!} />
              ) : (
                <ResponsiveImage srcSet={artwork?.srcSet} aspect={artwork?.aspect!} />
              )}
            </div>
          ))}
        </div>
      </div>
    </StaggerTransitionRoot>
  )
})

function MetaRow(props: PropsWithChildren<{ label: string }>) {
  return (
    <div className="grid-auto type-body-s-roman el-a:underline el-a:underline-offset-1 el-a:decoration-grey">
      <div className="col-span-6 md:col-span-2">{props.label}</div>
      <div className="col-span-6 md:col-span-2">{props.children}</div>
    </div>
  )
}

function getContributorLabel(type: string | null) {
  if (type) {
    let result = type
      .replace(/_/g, " ")
      .replace(/ *[0-9]$/g, "")
      .replace(/ ([a-z])/g, (str, match) => " " + match.toUpperCase())

    return result.charAt(0).toUpperCase() + result.slice(1)
  }

  return null
}
